// Bottom sheet primitive for the Season Dashboard (player detail, full tables).
// motion/react spring slide-up over a scrim, matching the approved mockup.

import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X } from 'lucide-react';
import { GS } from '../../lib/designTokens';

export const Sheet = ({ open, onClose, title, sub, children }: {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  sub?: string;
  children: React.ReactNode;
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="scrim"
            className="fixed inset-0 z-40"
            style={{ background: 'rgba(20,26,33,0.42)' }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            key="sheet"
            role="dialog"
            aria-modal="true"
            className="fixed left-0 right-0 bottom-0 z-50 rounded-t-3xl overflow-y-auto overscroll-contain"
            style={{ background: GS.bg, maxHeight: '86dvh', boxShadow: '0 -12px 40px rgba(20,26,33,0.25)' }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 32, stiffness: 320 }}
          >
            <div className="w-10 h-1 rounded-full mx-auto mt-2.5" style={{ background: GS.ink3, opacity: 0.4 }} />
            {(title || sub) && (
              <div className="flex items-center gap-3 px-5 pt-4 pb-3">
                <div className="min-w-0 flex-1">
                  {title && <div className="text-lg font-black italic truncate" style={{ color: GS.ink }}>{title}</div>}
                  {sub && <div className="text-[11px] font-bold mt-0.5" style={{ color: GS.ink2 }}>{sub}</div>}
                </div>
                <button
                  onClick={onClose}
                  aria-label="Close"
                  className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: GS.panel2, border: `1px solid ${GS.line}`, color: GS.ink2 }}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            <div className="px-4 pb-8">{children}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
